import React from 'react';
import {
  Card,
  CardBody,
  Typography,
  Chip,
  Checkbox,
  Spinner,
} from '@material-tailwind/react';
import {
  TableCellsIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatHours = (value) => {
  if (!value && value !== 0) return 'N/A';
  return value.toLocaleString();
};

function RecentlySoldCompsSection({
  content,
  allSales,
  excludedSalesComps = [],
  onToggleExclusion,
  isUpdating = false,
}) {
  if (!content && (!allSales || allSales.length === 0)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No recently sold data available</Typography>
      </div>
    );
  }

  const { transactions, summary, selectionCriteria } = content || {};

  // Use allSales if provided, otherwise fall back to transactions
  const displaySales = allSales || transactions || [];

  // Helper to check if a sale is excluded
  const isExcluded = (reg) =>
    excludedSalesComps?.includes(reg?.toUpperCase()) ?? false;

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {summary.totalTransactions !== undefined && (
            <Card className="bg-green-50">
              <CardBody className="p-2 text-center">
                <Typography variant="small" color="gray" className="text-xs">
                  Total Sales
                </Typography>
                <Typography variant="h6" color="green">
                  {summary.totalTransactions}
                </Typography>
              </CardBody>
            </Card>
          )}
          {summary.avgSalePrice !== undefined && (
            <Card className="bg-blue-50">
              <CardBody className="p-2 text-center">
                <Typography variant="small" color="gray" className="text-xs">
                  Avg Sale Price
                </Typography>
                <Typography variant="small" color="blue" className="font-bold">
                  {formatPrice(summary.avgSalePrice, summary.currency)}
                </Typography>
              </CardBody>
            </Card>
          )}
          {summary.avgDaysOnMarket !== undefined && (
            <Card className="bg-amber-50">
              <CardBody className="p-2 text-center">
                <Typography variant="small" color="gray" className="text-xs">
                  Avg DOM
                </Typography>
                <Typography variant="small" color="amber" className="font-bold">
                  {Math.round(summary.avgDaysOnMarket)} days
                </Typography>
              </CardBody>
            </Card>
          )}
          {summary.dateRange && (
            <Card className="bg-gray-50">
              <CardBody className="p-2 text-center">
                <Typography variant="small" color="gray" className="text-xs">
                  Date Range
                </Typography>
                <Typography variant="small" color="blue-gray" className="font-medium text-xs">
                  {summary.dateRange}
                </Typography>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Selection Criteria */}
      {selectionCriteria && (
        <Card className="bg-gray-50">
          <CardBody className="p-2">
            <Typography variant="small" className="font-medium text-xs mb-1">
              Selection Criteria
            </Typography>
            <Typography variant="small" color="gray" className="whitespace-pre-wrap text-xs">
              {selectionCriteria}
            </Typography>
          </CardBody>
        </Card>
      )}

      {/* Transactions Table */}
      {displaySales && displaySales.length > 0 ? (
        <Card>
          <CardBody className="p-0">
            <div className="flex items-center justify-between px-2 py-1 border-b">
              <div className="flex items-center gap-2">
                <TableCellsIcon className="h-4 w-4 text-gray-500" />
                <Typography variant="small" className="font-medium text-xs">
                  Sales Transactions
                </Typography>
                <Chip
                  size="sm"
                  value={`${displaySales.length - excludedSalesComps.length}/${displaySales.length}`}
                  variant="ghost"
                  className="text-xs"
                />
                {excludedSalesComps.length > 0 && (
                  <Chip
                    size="sm"
                    value={`${excludedSalesComps.length} excluded`}
                    variant="ghost"
                    color="amber"
                    className="text-xs"
                  />
                )}
              </div>
              {isUpdating && <Spinner className="h-4 w-4" />}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-max table-auto text-left text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    {onToggleExclusion && (
                      <th className="px-2 py-1 w-8">
                        <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                          Inc
                        </Typography>
                      </th>
                    )}
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Reg
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Year
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Model
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Hours
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Sale Price
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Sold
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        DOM
                      </Typography>
                    </th>
                    <th className="px-2 py-1">
                      <Typography variant="small" className="font-semibold text-gray-700 text-xs">
                        Notes
                      </Typography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displaySales.map((tx, index) => {
                    // Handle field name variations from backend
                    const reg = tx.reg || tx.registration || tx.tailNumber;
                    const saleDate = tx.dtSold || tx.saleDate || tx.sale_date || tx.soldDate;
                    const dom = tx.dom ?? tx.daysOnMarket ?? tx.days_on_market;
                    const hours = tx.totalTime || tx.total_time || tx.hours;
                    const price = tx.salePriceUsd || tx.salePrice || tx.sale_price || tx.price_usd || tx.price;
                    const modelYear = tx.year || tx.model_year;
                    const excluded = isExcluded(reg);

                    return (
                      <tr
                        key={tx.listingId || tx.id || index}
                        className={`border-b hover:bg-gray-50 ${excluded ? 'bg-gray-100 opacity-60' : ''}`}
                      >
                        {onToggleExclusion && (
                          <td className="px-2 py-1">
                            <Checkbox
                              checked={!excluded}
                              onChange={() => onToggleExclusion(reg)}
                              disabled={isUpdating}
                              containerProps={{ className: 'p-0' }}
                              className="h-4 w-4"
                            />
                          </td>
                        )}
                        <td className="px-2 py-1">
                          <span className={`font-medium text-xs ${excluded ? 'line-through text-gray-400' : ''}`}>
                            {reg || 'N/A'}
                          </span>
                        </td>
                        <td className={`px-2 py-1 text-xs ${excluded ? 'text-gray-400' : ''}`}>
                          {modelYear || 'N/A'}
                        </td>
                        <td className={`px-2 py-1 text-xs ${excluded ? 'text-gray-400' : ''}`}>
                          {tx.model || 'N/A'}
                        </td>
                        <td className={`px-2 py-1 text-xs ${excluded ? 'text-gray-400' : ''}`}>
                          {formatHours(hours)}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`font-medium text-xs ${excluded ? 'text-gray-400' : 'text-green-600'}`}>
                            {formatPrice(price, tx.currency)}
                          </span>
                        </td>
                        <td className={`px-2 py-1 text-xs ${excluded ? 'text-gray-400' : ''}`}>
                          {formatDate(saleDate)}
                        </td>
                        <td className={`px-2 py-1 text-xs ${excluded ? 'text-gray-400' : ''}`}>
                          {dom !== undefined && dom !== null ? `${dom}` : '-'}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`text-xs max-w-[150px] truncate block ${excluded ? 'text-gray-400' : 'text-gray-600'}`} title={tx.notes}>
                            {tx.notes || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <Typography>No recent sales transactions available</Typography>
        </div>
      )}
    </div>
  );
}

export default RecentlySoldCompsSection;
